/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$checkbox-label-margin-top: 0px !default;
$checkbox-small-label-margin-top: 0px !default;
$checkbox-size: 22px !default;
$checkbox-small-size: 22px !default;
$checkbox-border-width: 2px !default;
$checkbox-small-border-width: 2px !default;
$checkbox-border-radius: 6px !default;
$checkbox-small-border-radius: 6px !default;
$checkbox-label-font-size: 18px !default;
$checkbox-small-label-font-size: 14px !default;
$checkbox-label-line-height: 27px !default;
$checkbox-small-label-line-height: 21px !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

:host {
  display: inline-block;
}

:host([disabled]:not([disabled="false"])) {
  cursor: not-allowed;
  label {
    pointer-events: none;
  }
}

::slotted(eon-ui-rte-renderer) {
  --rteRendererLineHeight: #{$checkbox-label-line-height} !important;
}

.#{$prefix}checkbox {
  display: flex;
  align-items: flex-start;
  position: relative;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }

  .#{$prefix}checkbox-info-icon {
    margin-left: 12px;
    @include iconTextAligner($checkbox-size, 20px);
  }

  label {
    position: relative;
    width: auto;
    max-width: 100%;
    font-family: $font-family-base;
    cursor: pointer;
    outline: none;
    display: inline-flex;
    justify-content: flex-start;
    align-items: flex-start;
    box-sizing: border-box;
    font-size: $checkbox-label-font-size;
    line-height: $checkbox-label-line-height;
    color: v(labelColor);
    margin-bottom: 0;

    /* hide default html checkbox */
    .#{$prefix}native-control {
      display: none;
    }

    .#{$prefix}focus-outline {
      position: absolute;
      top: -5px;
      left: -5px;
      height: calc(100% + 10px);
      width: calc(100% + 10px);
      border: 2px solid v(focusColor);
      border-radius: 10px;
      opacity: 0;
      pointer-events: none;
      user-select: none;
    }

    .#{$prefix}icon-carrier {
      position: relative;
      width: $checkbox-size;
      height: $checkbox-size;
      display: flex;
      justify-content: center;
      flex-shrink: 0;
      flex-grow: 0;
      background-color: v(backgroundColor);
      box-shadow: inset 0px 0px 0px $checkbox-border-width v(borderColor);
      border-radius: $checkbox-border-radius;
      transition-property: box-shadow, background-color;
      transition-timing-function: ease-in-out;
      transition-duration: 150ms;

      &:focus-visible ~ .#{$prefix}focus-outline {
        opacity: 1;
      }

      .#{$prefix}icon-content {
        fill: v(iconColor);
        visibility: hidden;
        opacity: 0;
        transition-property: visibility, opacity, fill;
        transition-timing-function: ease-in-out;
        transition-duration: 150ms;
      }

      @media (pointer: fine) {
        &:hover,
        &.force-hover {
          background-color: v(backgroundColorHover);
          box-shadow: inset 0px 0px 0px calc($checkbox-border-width + 1px) v(borderColorHover);

          .#{$prefix}icon-content {
            fill: v(iconColorHover);
            visibility: visible;
            opacity: 0.5;
          }
        }
      }

      &.#{$prefix}checked {
        background-color: v(backgroundColorSelected);
        box-shadow: inset 0px 0px 0px $checkbox-border-width v(borderColorSelected);

        .#{$prefix}icon-content {
          fill: v(iconColorSelected);
          visibility: visible;
          opacity: 1;
        }

        @media (pointer: fine) {
          &:hover,
          &.force-hover {
            background-color: v(backgroundColorSelectedHover);
            box-shadow: inset 0px 0px 0px calc($checkbox-border-width + 1px) v(borderColorSelectedHover);

            .#{$prefix}icon-content {
              fill: v(iconColorSelectedHover);
              visibility: visible;
              opacity: 1;
            }
          }
        }

        &.#{$prefix}invalid {
          background-color: v(errorColor);
        }
      }

      &.#{$prefix}disabled {
        opacity: 0.5;
        background-color: v(backgroundColorDisabled);
        box-shadow: inset 0px 0px 0px $checkbox-border-width v(borderColorDisabled);

        .#{$prefix}icon-content {
          fill: v(iconColorDisabled);
        }

        &.#{$prefix}checked {
          background-color: v(backgroundColorSelectedDisabled);
          box-shadow: inset 0px 0px 0px $checkbox-border-width v(borderColorSelectedDisabled);

          .#{$prefix}icon-content {
            fill: v(iconColorSelectedDisabled);
            visibility: visible;
            opacity: 1;
          }
        }
      }

      &.#{$prefix}invalid {
        box-shadow: inset 0px 0px 0px $checkbox-border-width v(errorColor);

        &:not(.#{$prefix}checked) {
          @media (pointer: fine) {
            &.force-hover,
            &:hover {
              .#{$prefix}icon-content {
                fill: v(errorColor);
              }
            }
          }
        }

        @media (pointer: fine) {
          &:hover,
          &.force-hover {
            box-shadow: inset 0px 0px 0px calc($checkbox-border-width + 1px) v(errorColor);
          }
        }

        &.#{$prefix}checked {
          .#{$prefix}icon-content {
            visibility: visible;
            opacity: 1;
          }
        }
      }

      &:focus-visible {
        outline: none !important;
      }

      @media (pointer: fine) {
        &:hover,
        &.force-hover {
          & ~ .#{$prefix}label {
            text-decoration: underline;
          }
        }
      }
    }

    .#{$prefix}label {
      position: relative;
      @include iconTextAligner($checkbox-size, $checkbox-label-line-height);
      margin-bottom: calc(($checkbox-size - $checkbox-label-line-height) / 2);
      margin-left: 8px;
      top: $checkbox-label-margin-top;
      color: v(labelColor);
      white-space: normal;

      &.#{$prefix}disabled {
        color: v(labelColorDisabled);
        opacity: 0.5;
      }
    }
  }

  &.#{$prefix}focus-style .#{$prefix}icon-carrier ~ .#{$prefix}label {
    text-decoration: none !important;
  }

  &.#{$prefix}small {
    .#{$prefix}checkbox-info-icon {
      @include iconTextAligner($checkbox-small-size, 20px);
    }

    label {
      font-size: $checkbox-small-label-font-size; // 16px
      line-height: $checkbox-small-label-line-height; // 24px
      min-height: unset;

      .#{$prefix}label {
        @include iconTextAligner($checkbox-small-size, $checkbox-small-label-line-height);
        margin-bottom: calc(($checkbox-small-size - $checkbox-small-label-line-height) / 2);
        top: $checkbox-small-label-margin-top;
      }

      .#{$prefix}icon-carrier {
        border-radius: $checkbox-small-border-radius;
        width: $checkbox-small-size;
        height: $checkbox-small-size;

        @media (pointer: fine) {
          &:hover,
          &.force-hover {
            box-shadow: inset 0px 0px 0px calc($checkbox-small-border-width + 1px) v(borderColorHover);
          }
        }

        &.#{$prefix}checked {
          box-shadow: inset 0px 0px 0px $checkbox-small-border-width v(borderColorSelected);
        }

        &.#{$prefix}disabled {
          box-shadow: inset 0px 0px 0px $checkbox-small-border-width v(borderColorDisabled);

          &.#{$prefix}checked {
            box-shadow: inset 0px 0px 0px $checkbox-small-border-width v(borderColorSelectedDisabled);

            @media (pointer: fine) {
              &:hover,
              &.force-hover {
                box-shadow: inset 0px 0px 0px calc($checkbox-small-border-width + 1px) v(borderColorSelectedHover);
              }
            }
          }
        }

        &.#{$prefix}invalid {
          box-shadow: inset 0px 0px 0px $checkbox-small-border-width v(errorColor);
        }
      }
    }
  }

  &.#{$prefix}alt label .#{$prefix}icon-carrier.force-hover {
    box-shadow: inset 0px 0px 0px $checkbox-small-border-width v(borderColorSelected);
    background-color: v(backgroundColorSelected);

    .#{$prefix}icon-content {
      fill: v(iconColorSelected);
    }
  }
}